<template>
  <div>
    <div v-if="status === 'loading'" class="py-6 text-center w-full">
      <UiIcon class="animate-spin" icon="loader" /> {{ $t('feedback.loading') }}
    </div>
    <p v-else-if="status === 'error'" class="py-6 text-center text-red-600 dark:text-red-400">
      {{ t('loadError') }}
    </p>
    <div v-show="status === 'ready'">
      <UiAlert type="success" :text="t('description')" class="mb-6" />
      <div :id="targetId" />
    </div>
  </div>
</template>

<script lang="ts">
/** Valor original do conteúdo do dropdown */
const SEGMENTS: Record<string, string> = {
  'Agroindústria': 'agroindustry',
  'Algodoeira': 'cottonGin',
  'Armazéns de Grãos': 'grainWarehouses',
  'Armazéns Gerais': 'generalWarehouses',
  'Autocenter': 'autoCenter',
  'Avícola': 'poultry',
  'Cerealista': 'grainTrader',
  'Consultoria Agro': 'agroConsulting',
  'Cooperativa Agropecuária': 'agriculturalCooperative',
  'Distribuidor de Insumos': 'inputDistributor',
  'Distribuidora de Peças': 'partsDistributor',
  'Indústria de Fertilizantes': 'fertilizerIndustry',
  'Indústria de Nutrição Animal': 'animalNutritionIndustry',
  'Indústria Química': 'chemicalIndustry',
  'Irrigação': 'irrigation',
  'Loja Agropecuária': 'agriculturalStore',
  'Loja de Peças': 'partsStore',
  'Peças Autopeças': 'autoParts',
  'Pecuária': 'livestock',
  'Pneus': 'tires',
  'Psicultura': 'fishFarming',
  'Produção Agrícola': 'agriculturalProduction',
  'Sementeira': 'seedCompany',
  'Suinocultura': 'pigFarming',
  'Outros': 'other'
}

/**
 * Textos do formulário de indicação. O formulário no HubSpot é configurado em pt-br, então os textos
 * são trocados após a renderização. As chaves de `fields` são os nomes internos dos campos no HubSpot.
 */
const pt = {
  button: 'Indicar a Plataforma',
  title: 'Indique a Plataforma a um Amigo',
  description: 'Conhece uma empresa que pode se beneficiar da nossa plataforma? Indique agora e ajude a transformar a educação de mais pessoas!',
  loadError: 'Não foi possível carregar o formulário. Tente novamente em alguns instantes.',
  submit: 'Enviar',
  selectPlaceholder: 'Selecione',
  submittedMessage: 'Obrigado por enviar o formulário.',
  fields: {
    nome_de_quem_indicou: 'Qual o seu nome?',
    email_de_quem_indicou: 'Qual o seu email?',
    telefone_de_quem_indicou: 'Qual o seu telefone?',
    nome_da_empresa_indicada: 'Nome da sua empresa?',
    firstname: 'Nome do indicado',
    company: 'Nome da empresa',
    email: 'E-mail do indicado',
    phone: 'Telefone do indicado',
    segmento: 'Segmento do indicado',
    message: 'Mensagem'
  },
  segments: {
    agroindustry: 'Agroindústria',
    cottonGin: 'Algodoeira',
    grainWarehouses: 'Armazéns de Grãos',
    generalWarehouses: 'Armazéns Gerais',
    autoCenter: 'Autocenter',
    poultry: 'Avícola',
    grainTrader: 'Cerealista',
    agroConsulting: 'Consultoria Agro',
    agriculturalCooperative: 'Cooperativa Agropecuária',
    inputDistributor: 'Distribuidor de Insumos',
    partsDistributor: 'Distribuidora de Peças',
    fertilizerIndustry: 'Indústria de Fertilizantes',
    animalNutritionIndustry: 'Indústria de Nutrição Animal',
    chemicalIndustry: 'Indústria Química',
    irrigation: 'Irrigação',
    agriculturalStore: 'Loja Agropecuária',
    partsStore: 'Loja de Peças',
    autoParts: 'Peças Autopeças',
    livestock: 'Pecuária',
    tires: 'Pneus',
    fishFarming: 'Piscicultura',
    agriculturalProduction: 'Produção Agrícola',
    seedCompany: 'Sementeira',
    pigFarming: 'Suinocultura',
    other: 'Outros'
  }
}

const en: typeof pt = {
  button: 'Refer the Platform',
  title: 'Refer the Platform to a Friend',
  description: 'Do you know a company that could benefit from our platform? Refer them now and help transform education for more people!',
  loadError: 'We couldn\'t load the form. Please try again in a few moments.',
  submit: 'Submit',
  selectPlaceholder: 'Select',
  submittedMessage: 'Thank you for submitting the form.',
  fields: {
    nome_de_quem_indicou: 'What is your name?',
    email_de_quem_indicou: 'What is your email?',
    telefone_de_quem_indicou: 'What is your phone number?',
    nome_da_empresa_indicada: 'What is your company name?',
    firstname: 'Referral\'s name',
    company: 'Company name',
    email: 'Referral\'s email',
    phone: 'Referral\'s phone number',
    segmento: 'Referral\'s industry',
    message: 'Message'
  },
  segments: {
    agroindustry: 'Agribusiness',
    cottonGin: 'Cotton Gin',
    grainWarehouses: 'Grain Warehouses',
    generalWarehouses: 'General Warehouses',
    autoCenter: 'Auto Center',
    poultry: 'Poultry',
    grainTrader: 'Grain Trader',
    agroConsulting: 'Agricultural Consulting',
    agriculturalCooperative: 'Agricultural Cooperative',
    inputDistributor: 'Agricultural Input Distributor',
    partsDistributor: 'Parts Distributor',
    fertilizerIndustry: 'Fertilizer Industry',
    animalNutritionIndustry: 'Animal Nutrition Industry',
    chemicalIndustry: 'Chemical Industry',
    irrigation: 'Irrigation',
    agriculturalStore: 'Farm Supply Store',
    partsStore: 'Parts Store',
    autoParts: 'Auto Parts',
    livestock: 'Livestock',
    tires: 'Tires',
    fishFarming: 'Fish Farming',
    agriculturalProduction: 'Agricultural Production',
    seedCompany: 'Seed Company',
    pigFarming: 'Pig Farming',
    other: 'Other'
  }
}

const es: typeof pt = {
  button: 'Recomendar la Plataforma',
  title: 'Recomienda la Plataforma a un Amigo',
  description: '¿Conoces una empresa que podría beneficiarse de nuestra plataforma? ¡Recomiéndala ahora y ayuda a transformar la educación de más personas!',
  loadError: 'No fue posible cargar el formulario. Inténtalo de nuevo en unos instantes.',
  submit: 'Enviar',
  selectPlaceholder: 'Selecciona',
  submittedMessage: 'Gracias por enviar el formulario.',
  fields: {
    nome_de_quem_indicou: '¿Cuál es tu nombre?',
    email_de_quem_indicou: '¿Cuál es tu correo electrónico?',
    telefone_de_quem_indicou: '¿Cuál es tu teléfono?',
    nome_da_empresa_indicada: '¿Cuál es el nombre de tu empresa?',
    firstname: 'Nombre del recomendado',
    company: 'Nombre de la empresa',
    email: 'Correo electrónico del recomendado',
    phone: 'Teléfono del recomendado',
    segmento: 'Segmento del recomendado',
    message: 'Mensaje'
  },
  segments: {
    agroindustry: 'Agroindustria',
    cottonGin: 'Desmotadora de Algodón',
    grainWarehouses: 'Almacenes de Granos',
    generalWarehouses: 'Almacenes Generales',
    autoCenter: 'Centro Automotriz',
    poultry: 'Avícola',
    grainTrader: 'Acopiador de Granos',
    agroConsulting: 'Consultoría Agrícola',
    agriculturalCooperative: 'Cooperativa Agropecuaria',
    inputDistributor: 'Distribuidor de Insumos',
    partsDistributor: 'Distribuidora de Repuestos',
    fertilizerIndustry: 'Industria de Fertilizantes',
    animalNutritionIndustry: 'Industria de Nutrición Animal',
    chemicalIndustry: 'Industria Química',
    irrigation: 'Riego',
    agriculturalStore: 'Tienda Agropecuaria',
    partsStore: 'Tienda de Repuestos',
    autoParts: 'Autopartes',
    livestock: 'Ganadería',
    tires: 'Neumáticos',
    fishFarming: 'Piscicultura',
    agriculturalProduction: 'Producción Agrícola',
    seedCompany: 'Semillera',
    pigFarming: 'Porcicultura',
    other: 'Otros'
  }
}

export const hubspotFormMessages: Record<string, typeof pt> = { pt, en, es }

/** Idioma da aplicação → idioma do HubSpot (usado nas mensagens de validação dele) */
const HUBSPOT_LOCALES: Record<string, string> = { pt: 'pt-br', en: 'en', es: 'es' }
</script>

<script setup lang="ts">
import { onMounted, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSystemStore } from '@/stores'
import { loadHubspotForms } from '@/composables/useHubspotForms'

import { UiIcon } from '@/components/ui/'
import UiAlert from '@/components/ui/alert.vue'

const props = withDefaults(defineProps<{
  portalId: string
  formId: string
  region?: string
  /** Valores iniciais, indexados pelo nome interno do campo no HubSpot */
  values?: Record<string, string | null | undefined>
}>(), {
  region: 'na1',
  values: () => ({})
})

const systemStore = useSystemStore()
// Escopo local: as mensagens ficam neste arquivo e o idioma acompanha o global
const { t, locale } = useI18n({ useScope: 'local', messages: hubspotFormMessages })

const targetId = `hs-form-${useId()}`
const status = ref<'loading' | 'ready' | 'error'>('loading')
const formEl = ref<HTMLFormElement>()

/**
 * O HubSpot pode renderizar o formulário dentro de um iframe, onde as variáveis CSS da aplicação
 * não chegam. Por isso as cores são resolvidas aqui e enviadas como valores literais.
 */
const buildCss = () => {
  const rootStyle = getComputedStyle(document.documentElement)
  const primary = rootStyle.getPropertyValue('--c-primary-600').trim() || '#757575'
  const onPrimary = rootStyle.getPropertyValue('--c-primary-at-600').trim() || '#ffffff'

  const c = systemStore.isDark
    ? { text: '#d4d4d4', muted: '#a3a3a3', input: '#262626', border: '#525252' }
    : { text: '#262626', muted: '#737373', input: '#ffffff', border: '#d4d4d4' }

  // O `!important` é necessário porque, fora do iframe, estilos globais da aplicação (ex.: `.input`) vazam para o formulário
  return `
    .hs-form { font-family: 'Archivo', sans-serif; font-size: 14px; color: ${c.text}; color-scheme: ${systemStore.isDark ? 'dark' : 'light'}; }
    .hs-form *, .hs-form *::before, .hs-form *::after { box-sizing: border-box; }

    .hs-form fieldset {
      display: grid; grid-template-columns: 1fr; gap: 0 16px;
      max-width: none !important; margin: 0; padding: 0; border: 0;
    }
    .hs-form fieldset.form-columns-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .hs-form fieldset.form-columns-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    @media (max-width: 560px) {
      .hs-form fieldset.form-columns-2, .hs-form fieldset.form-columns-3 { grid-template-columns: 1fr; }
    }

    .hs-form .hs-form-field { width: auto !important; float: none !important; padding: 0 !important; margin: 0 0 16px; }
    .hs-form .hs-form-field > label { display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: ${c.text}; }
    .hs-form .hs-form-required { margin-left: 2px; color: #ef4444; }
    .hs-form legend, .hs-form .hs-richtext { color: ${c.muted}; font-size: 12px; }

    .hs-form .input {
      margin: 0 !important; padding: 0 !important; height: auto !important; min-height: 0 !important;
      background: none !important; border: 0 !important; box-shadow: none !important;
    }
    .hs-form .hs-input {
      display: block; width: 100% !important; height: 40px; margin: 0; padding: 8px 12px;
      font-family: inherit; font-size: 14px; line-height: 20px; color: ${c.text}; background: ${c.input};
      border: 1px solid ${c.border}; border-radius: 8px; outline: none; transition: border-color .15s, box-shadow .15s;
    }
    .hs-form textarea.hs-input { height: auto; min-height: 112px; resize: vertical; }
    .hs-form .hs-input::placeholder { color: ${c.muted}; }
    .hs-form .hs-input:focus { border-color: ${primary}; box-shadow: 0 0 0 1px ${primary}; }
    .hs-form .hs-input.invalid.error { border-color: #ef4444; }

    .hs-form .hs-error-msgs { list-style: none; padding: 0; margin: 4px 0 0; }
    .hs-form .hs-error-msg, .hs-form .hs-main-font-element { display: block; margin: 0; color: #ef4444; font-size: 12px; font-weight: 400; }

    .hs-form .hs_submit { margin-top: 8px; display: flex; justify-content: flex-end; }
    .hs-form .actions { margin: 0; padding: 0; }
    .hs-form .hs-button {
      cursor: pointer; padding: 12px 32px; font-family: inherit; font-size: 14px; font-weight: 600; text-transform: uppercase;
      color: ${onPrimary}; background: ${primary}; border: none; border-radius: 8px; transition: opacity .15s;
    }
    .hs-form .hs-button:hover { opacity: .9; }
    .submitted-message { font-family: 'Archivo', sans-serif; font-size: 14px; color: ${c.text}; text-align: center; padding: 24px 0; }
  `
}

/**
 * O formulário do HubSpot controla o próprio estado, então atribuir `value` não basta: é preciso usar o
 * setter nativo e disparar os eventos para que ele reconheça o valor (inclusive na validação).
 */
const fillFields = () => {
  if (!formEl.value) return

  Object.entries(props.values).forEach(([name, value]) => {
    const field = formEl.value.querySelector<HTMLInputElement>(`[name="${name}"]`)
    if (!field || !value || field.value) return

    Object.getOwnPropertyDescriptor(Object.getPrototypeOf(field), 'value')?.set?.call(field, value)
    field.dispatchEvent(new Event('input', { bubbles: true }))
    field.dispatchEvent(new Event('change', { bubbles: true }))
  })
}

/** Troca os textos vindos do HubSpot (pt-br) pelos do idioma atual; os valores enviados não mudam */
const translateForm = () => {
  if (!formEl.value) return

  Object.keys(pt.fields).forEach((name) => {
    const label = formEl.value.querySelector(`.hs_${name} > label span:first-child`)
    if (label) label.textContent = t(`fields.${name}`)
  })

  formEl.value.querySelectorAll<HTMLOptionElement>('select[name="segmento"] option').forEach((option) => {
    if (!option.value) option.textContent = t('selectPlaceholder')
    else if (SEGMENTS[option.value]) option.textContent = t(`segments.${SEGMENTS[option.value]}`)
  })

  const submit = formEl.value.querySelector<HTMLInputElement>('input[type="submit"]')
  if (submit) submit.value = t('submit')
}

// Os valores podem chegar depois que o formulário já foi renderizado (ex.: dados ainda carregando da API)
watch(() => props.values, fillFields, { deep: true })

onMounted(async () => {
  try {
    const forms = await loadHubspotForms()

    forms.create({
      portalId: props.portalId,
      formId: props.formId,
      region: props.region,
      target: `#${targetId}`,
      css: buildCss(),
      locale: HUBSPOT_LOCALES[locale.value] || HUBSPOT_LOCALES.pt,
      onFormReady: ($form) => {
        // Com jQuery na página o HubSpot entrega um objeto jQuery em vez do elemento
        formEl.value = typeof $form?.querySelector === 'function' ? $form : $form?.[0]
        translateForm()
        fillFields()
        status.value = 'ready'
      },
      onFormSubmitted: () => {
        const message = document.querySelector(`#${targetId} .submitted-message`)
        if (message) message.textContent = t('submittedMessage')
      }
    })
  } catch {
    status.value = 'error'
  }
})
</script>
